import { useLocalStorage } from '@mantine/hooks';
import { useEffect, useMemo } from 'react';

import { InsightTag } from '@ndvr/customer-api';

const LS_KEY_TAG_ORDER = 'proactive-insights-tag-order';
const LS_KEY_HIDDEN_TAGS = 'proactive-insights-hidden-tags';

export const DEFAULT_TAG_ORDER: InsightTag[] = [
    InsightTag.R1,
    InsightTag.E1,
    InsightTag.E2,
    InsightTag.E3,
    InsightTag.E4,
    InsightTag.N1,
    InsightTag.N2,
    InsightTag.N3,
    InsightTag.N4,
    InsightTag.N5,
    InsightTag.N6,
    InsightTag.R2,
    InsightTag.R3,
];

// Reconciles a persisted tag order with the current set of insight tags: keeps the
// user's stored ordering, appends any tags added since they last saved (in default
// position), and drops tags that no longer exist. This lets new insight types show up
// without forcing existing users to hit Reset.
const reconcileTagOrder = (stored: InsightTag[]): InsightTag[] => {
    const known = stored.filter((tag) => DEFAULT_TAG_ORDER.includes(tag));
    const missing = DEFAULT_TAG_ORDER.filter((tag) => !known.includes(tag));
    return [...known, ...missing];
};

export const useInsightSettings = () => {
    const [storedTagOrder, setTagOrder] = useLocalStorage<InsightTag[]>({
        key: LS_KEY_TAG_ORDER,
        defaultValue: DEFAULT_TAG_ORDER,
    });
    const [hiddenTags, setHiddenTags] = useLocalStorage<InsightTag[]>({
        key: LS_KEY_HIDDEN_TAGS,
        defaultValue: [],
    });

    const tagOrder = useMemo(() => reconcileTagOrder(storedTagOrder), [storedTagOrder]);

    // Persist the reconciled order back so the stored value stays in sync once new
    // tags have been merged in (or stale ones removed).
    useEffect(() => {
        const changed =
            tagOrder.length !== storedTagOrder.length || tagOrder.some((tag, i) => tag !== storedTagOrder[i]);
        if (changed) {
            setTagOrder(tagOrder);
        }
    }, [tagOrder, storedTagOrder, setTagOrder]);

    const toggleTagVisibility = (tag: InsightTag) =>
        setHiddenTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));

    const resetSettings = () => {
        setTagOrder(DEFAULT_TAG_ORDER);
        setHiddenTags([]);
    };

    return { tagOrder, setTagOrder, hiddenTags, toggleTagVisibility, resetSettings };
};
